import React from 'react';
import I18n from 'i18n-js';
import { formatBid, bumpRatePercentToDecimal } from './bump_helpers';
import {
  MAX_BUMP_AMOUNT_PERCENT,
  MIN_BUMP_AMOUNT_PERCENT,
  BUMP_INCREMENT_AMOUNT_PERCENT,
} from '../shared/constants';
import { RCAlertBox, RCIcon, RCRadioCardGroup, RCTag } from '@reverbdotcom/cadence/components';
import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';
import { LightningIcon, PencilSimpleIcon } from '@phosphor-icons/react';
import { RCTextUtility } from '@reverbdotcom/cadence/components/RCText/RCText';
import { withPrefix } from '@reverbdotcom/commons/src/i18n_helpers';

const RATE_VALUE_RECOMMENDED = 'recommended';
const RATE_VALUE_CUSTOM = 'custom';
const SELL_THROUGH_PERCENT = '25';

const t = withPrefix('discovery.bumpModal');

export function RecommendedRadioV3({
  recommendationAmount,
  selectedBid,
  handleBidChange,
  showCustomWarning,
  setShowCustomWarning,
  minimumBumpBid,
}: {
  recommendationAmount: number;
  selectedBid: number;
  handleBidChange: (bid: number) => void;
  showCustomWarning: boolean;
  setShowCustomWarning: (boolean) => void;
  minimumBumpBid?: number;
}) {
  const [usingCustom, setUsingCustom] = React.useState(
    (selectedBid !== 0 && ((bumpRatePercentToDecimal(recommendationAmount)) !== selectedBid)),
  );

  const [usingRecommended, setUsingRecommended] = React.useState(
    !usingCustom,
  );

  const [customBid, setCustomBid] = React.useState(
    (selectedBid !== 0) ? (formatBid(selectedBid)) : recommendationAmount,
  );

  React.useEffect(() => {
    if (usingRecommended) {
      handleBidChange(bumpRatePercentToDecimal(recommendationAmount));
    }
  }, [usingRecommended, recommendationAmount, handleBidChange]);

  const handleRecommendedClick = (): void => {
    setUsingRecommended(true);
    setUsingCustom(false);
    setShowCustomWarning(false);

    handleBidChange(bumpRatePercentToDecimal(recommendationAmount));
  };

  const handleManualClick = (): void => {
    setUsingRecommended(false);
    setUsingCustom(true);

    setCustomWarning(customBid);
    handleBidChange(bumpRatePercentToDecimal(customBid));
  };

  const handleCustomSelectChange = (event: any) => {
    const bid = event?.target?.value;
    setCustomWarning(bid);
    setCustomBid(bid);
    handleBidChange(bumpRatePercentToDecimal(bid));
  };

  const setCustomWarning = (bid: number) => {
    // The input can hand us an empty string when cleared, which would otherwise
    // coerce to a valid 0 and silently submit.
    const value = Number.parseFloat(String(bid));
    if (Number.isFinite(value) &&
      (value >= (minimumBumpBid ?? MIN_BUMP_AMOUNT_PERCENT)) &&
      (value <= MAX_BUMP_AMOUNT_PERCENT) &&
      (value % BUMP_INCREMENT_AMOUNT_PERCENT === 0)) {
      setShowCustomWarning(false);
    } else {
      setShowCustomWarning(true);
    }
  };

  const handleCardRateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    if (next === RATE_VALUE_RECOMMENDED) {
      handleRecommendedClick();
    } else {
      handleManualClick();
    }
  };

  return (
    <div className="d-flex fx-dir-col">
      <div className="bump-modal-top-pick__rate-cards">
        <RCRadioCardGroup
          id="bump-v3-rate"
          name="bump-v3-rate"
          value={usingRecommended ? RATE_VALUE_RECOMMENDED : RATE_VALUE_CUSTOM}
          onChange={handleCardRateChange}
          label={t('setRateIteration')}
          visuallyHiddenLabel
          iconPlacement="hidden"
          iconAlignment="top"
        >
          <RCRadioCardGroup.Option
            value={RATE_VALUE_RECOMMENDED}
            label={
              <div className="d-flex fx-align-center fx-justify-between mobile-fx-dir-col mobile-fx-align-start mobile-gap-1">
                <div className="d-flex fx-align-center gap-2">
                  <div className="fg-color-text-accent">
                    <RCIcon icon={<LightningIcon weight="duotone" />} size={5} />
                  </div>
                  <RCTextUtility size="500" weight="bold">
                    {`${recommendationAmount}%`}
                  </RCTextUtility>
                </div>
                <RCTag variant="highlight">
                  {t('recommended')}
                </RCTag>
              </div>
            }
            sublabel={
              <>
                <br />
                <RCTextUtility size="350" weight="semibold">
                  <Translate
                    text="discovery.bumpModal.sellThroughCalloutIteration"
                    args={{ percent: SELL_THROUGH_PERCENT }}
                  />
                </RCTextUtility>
                <RCTextUtility size="350" color="secondary">
                  <Translate text="discovery.bumpModal.sellThroughCalloutSubtitle" />
                </RCTextUtility>
              </>
            }
          />
          <RCRadioCardGroup.Option
            value={RATE_VALUE_CUSTOM}
            label={
              <div className="d-flex fx-align-center gap-2">
                <RCIcon icon={<PencilSimpleIcon />} size={5} />
                <RCTextUtility size="500" weight="bold">
                  {t('topPickCustomLabel')}
                </RCTextUtility>
              </div>
            }
            sublabel={
              <><br />
                <RCTextUtility size="350" weight="semibold">
                  {t('topPickCustomSublabel')}
                </RCTextUtility>
              </>
            }
          />
        </RCRadioCardGroup>
      </div>

      {usingCustom && (
        <div className="d-flex fx-dir-col mt-3">
          <div className="width-100">
            <div className="input-group">
              <input
                type="number"
                aria-label={t('customRate')}
                className="input-group__input"
                value={customBid}
                onChange={handleCustomSelectChange}
              />
              <div className="input-group__text">%</div>
            </div>
          </div>
          {showCustomWarning && (
            <div className="mt-2">
              <RCAlertBox type="error" small>
                {I18n.t('discovery.bumpModal.customBumpWarning', {
                  minBump: minimumBumpBid ?? MIN_BUMP_AMOUNT_PERCENT,
                  maxBump: MAX_BUMP_AMOUNT_PERCENT,
                  bumpIncrement: BUMP_INCREMENT_AMOUNT_PERCENT,
                })}
              </RCAlertBox>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
